import React, { useEffect, useState } from 'react';
import CardContainer from 'src/components/CardContainer/CardContainer';
import { clsx } from 'clsx';
import { useGetProjectByIdQuery, useLazyGetCoCustomerAggregatedQuery } from 'src/services';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { GoToButton } from 'src/components/ButtonWithIcon/GoToButton';
import { ROUTE_PATHS } from 'src/utils/route-paths';
import { WeeklyDateSelect } from 'src/pages/ControlDeskPage/WeeklyAnalyticsSection/WeeklyDateSelect';
import { WeeklyAnalyticsChart } from 'src/pages/ControlDeskPage/WeeklyAnalyticsSection/WeeklyAnalyticsChart';
import { API_VERSION } from 'src/constants/api-routes';

const INITIAL_STATE = {
  resolutionRate: {
    value: 'N/A',
  },
  totalProcessedTickets: {
    value: 'N/A',
  },
};

export const WeeklyAnalyticsSection = () => {
  const [dateRange, setDateRange] = React.useState(null);
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV1 = version === API_VERSION.V1;
  const [resolutionRate, setResolutionRate] = useState(INITIAL_STATE.resolutionRate);
  const [totalProcessedTickets, setTotalProcessedTickets] = useState(
    INITIAL_STATE.totalProcessedTickets,
  );

  const [fetchAggregatedQuery, { data: aggregatedData, isSuccess, isFetching, isError, error }] = (
    useLazyGetCoCustomerAggregatedQuery as any
  )({}, { skip: !dateRange?.start_time || !dateRange?.end_time });

  useEffect(() => {
    if (isV1 && currentProject && dateRange) {
      fetchAggregatedQuery({
        project_ids: [id],
        start_time: dateRange.start_time,
        end_time: dateRange.end_time,
        changes_percentage: true,
      });
    }
  }, [id, currentProject, dateRange]);

  useEffect(() => {
    if (aggregatedData) {
      setResolutionRate(aggregatedData.resolutionRate);
      setTotalProcessedTickets(aggregatedData.totalProcessedTickets);
    } else {
      setResolutionRate(INITIAL_STATE.resolutionRate);
      setTotalProcessedTickets(INITIAL_STATE.totalProcessedTickets);
    }
  }, [aggregatedData]);

  useEffect(() => {
    if (isError) {
      setResolutionRate(INITIAL_STATE.resolutionRate);
      setTotalProcessedTickets(INITIAL_STATE.totalProcessedTickets);
    }
  }, [isError]);

  return (
    <CardContainer className="!h-full flex flex-col justify-between">
      <div
        className={clsx(
          (!currentProject?.is_active || isInactivePlan) && 'opacity-30 pointer-events-none',
          'card-header z-50',
        )}
      >
        <div className="title cursor-default flex w-full justify-between">
          <div className="flex items-center gap-3.5 overflow-auto">
            <span className="!no-underline max-md:hidden text-sm font-bold text-[#7A6BE5] text-nowrap">
              Weekly analytics
            </span>
            <div className="flex">
              <div className="flex items-center gap-2">
                <p className="opacity-70 text-sm font-medium text-nowrap">Resolution rate</p>
                <p className="text-xl font-bold">{resolutionRate.value}%</p>
              </div>
              <div className="min-w-px h-11/12 bg-ebony opacity-10 mx-2.5" />
              <div className="flex items-center gap-2">
                <p className="opacity-70 text-sm font-medium text-nowrap">
                  Total processed tickets
                </p>
                <p className="text-xl font-bold">{totalProcessedTickets.value}</p>
              </div>
            </div>
          </div>
          {isV1 && <GoToButton href={ROUTE_PATHS.DASHBOARD_PROJECT_ANALYTICS.replace(':id', id)} />}
        </div>
      </div>
      <div
        className={clsx(
          'card-body  h-full flex w-full gap-2.5 max-md:flex-col !mt-[17px]  min-h-[260px]',
        )}
      >
        <WeeklyAnalyticsChart
          dateRange={dateRange}
          hideLabel
          dataKeyStack={'escalatedTickets'}
          dataKey={'resolvedTickets'}
          chartType={'barChart'}
          className={'bg-[#E5E6FF]'}
        />
      </div>
      <div className="w-full flex items-center justify-center">
        <WeeklyDateSelect setDateRange={setDateRange} />
      </div>
    </CardContainer>
  );
};
